import React from 'react'

// get width of an element
export const getWidth = (elem) => (elem && elem.offsetWidth) || 0
export const getHeight = (elem) => (elem && elem.offsetHeight) || 0

// startIndex that needs to be present
export const lazySlidesOnLeft = () => 1
export const lazySlidesOnRight = (spec) => spec.slidesToShow + 1
export const lazyStartIndex = (spec) => spec.currentSlide - lazySlidesOnLeft(spec)
export const lazyEndIndex = (spec) => spec.currentSlide + lazySlidesOnRight(spec)

export function clamp(number, lowerBound, upperBound) {
  return Math.max(lowerBound, Math.min(number, upperBound))
}

export const slidesOnRight = ({ slidesToShow, rtl }) => {
  if (rtl) {
    return 0
  }
  return slidesToShow - 1
}

export const slidesOnLeft = ({ slidesToShow, rtl }) => {
  if (rtl) {
    return slidesToShow - 1
  }
  return 0
}

export const getPreClones = (spec) => {
  if (spec.unslick || !spec.infinite) {
    return 0
  }
  return spec.slidesToShow
}

export const getPostClones = (spec) => {
  if (spec.unslick || !spec.infinite) {
    return 0
  }
  return spec.slideCount
}

export const getTotalSlides = (spec) =>
  spec.slideCount === 1 ? 1 : getPreClones(spec) + spec.slideCount + getPostClones(spec)

export const siblingDirection = (spec) => {
  if (spec.targetSlide > spec.currentSlide) {
    if (spec.targetSlide > spec.currentSlide + slidesOnRight(spec)) {
      return 'left'
    }
    return 'right'
  }
  if (spec.targetSlide < spec.currentSlide - slidesOnLeft(spec)) {
    return 'right'
  }
  return 'left'
}

export const checkSpecKeys = (spec, keysArray) =>
  // eslint-disable-next-line no-prototype-builtins
  keysArray.reduce((value, key) => value && spec.hasOwnProperty(key), true)
    ? null
    : // eslint-disable-next-line no-console
      console.error('Keys Missing:', spec)

export const getTrackLeft = (spec) => {
  if (spec.unslick) {
    return 0
  }

  checkSpecKeys(spec, [
    'slideIndex',
    'trackRef',
    'infinite',
    'slideCount',
    'slidesToShow',
    'slidesToScroll',
    'slideWidth',
    'listWidth',
    'slideHeight',
    'rtl',
  ])

  const {
    slideIndex,
    infinite,
    slideCount,
    slidesToShow,
    slidesToScroll,
    slideWidth,
    slideHeight,
    vertical,
    rtl,
  } = spec

  let slideOffset = 0
  let targetLeft
  let verticalOffset = 0

  if (spec.slideCount === 1) {
    return 0
  }

  const multiplier = rtl ? 1 : -1
  let slidesToOffset = 0
  if (infinite) {
    slidesToOffset = multiplier * getPreClones(spec) // bring active slide to the beginning of visual area
    // if next scroll doesn't have enough children, just reach till the end of original slides instead of shifting slidesToScroll children
    if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
      slidesToOffset = -(slideIndex > slideCount
        ? slidesToShow - (slideIndex - slideCount)
        : slideCount % slidesToScroll)
    }
  } else if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
    slidesToOffset = slidesToShow - (slideCount % slidesToScroll)
  }
  slideOffset = slidesToOffset * slideWidth
  verticalOffset = slidesToOffset * slideHeight

  if (!vertical) {
    targetLeft = slideOffset + multiplier * slideIndex * slideWidth
  } else {
    targetLeft = verticalOffset - slideIndex * slideHeight
  }

  return targetLeft
}

export const getTrackCSS = (spec) => {
  checkSpecKeys(spec, ['left', 'slideCount', 'slidesToShow', 'slideWidth'])
  let trackWidth
  let trackHeight
  const trackChildren = spec.slideCount + 2 * spec.slidesToShow
  if (!spec.vertical) {
    trackWidth = getTotalSlides(spec) * spec.slideWidth
  } else {
    trackHeight = trackChildren * spec.slideHeight
  }
  let style = {
    opacity: 1,
    transition: '',
  }
  const transform = !spec.vertical
    ? `translate3d(${spec.left}px, 0px, 0px)`
    : `translate3d(0px, ${spec.left}px, 0px)`
  style = {
    ...style,
    transform,
  }
  if (trackWidth) style.width = trackWidth
  if (trackHeight) style.height = trackHeight

  return style
}

export const getTrackAnimateCSS = (spec) => {
  checkSpecKeys(spec, ['left', 'slideCount', 'slidesToShow', 'slideWidth', 'speed', 'cssEase'])
  const style = getTrackCSS(spec)
  style.transition = `transform ${spec.speed}ms ${spec.cssEase}`
  return style
}

export const getSlideCount = (spec) => {
  const centerOffset = 0
  let swipedSlide
  const slickList = spec.listRef
  const slides = (slickList.querySelectorAll && slickList.querySelectorAll('.slick-slide')) || []
  Array.from(slides).every((slide) => {
    if (!spec.vertical) {
      if (slide.offsetLeft - centerOffset + getWidth(slide) / 2 > spec.swipeLeft * -1) {
        swipedSlide = slide
        return false
      }
    } else if (slide.offsetTop + getHeight(slide) / 2 > spec.swipeLeft * -1) {
      swipedSlide = slide
      return false
    }

    return true
  })

  if (!swipedSlide) {
    return 0
  }
  const currentIndex = spec.rtl === true ? spec.slideCount - spec.currentSlide : spec.currentSlide
  return Math.abs(swipedSlide.dataset.index - currentIndex) || 1
}

export const getNavigableIndexes = (spec) => {
  const max = spec.infinite ? spec.slideCount * 2 : spec.slideCount
  let breakpoint = spec.infinite ? spec.slidesToShow * -1 : 0
  let counter = spec.infinite ? spec.slidesToShow * -1 : 0
  const indexes = []
  while (breakpoint < max) {
    indexes.push(breakpoint)
    breakpoint = counter + spec.slidesToScroll
    counter += Math.min(spec.slidesToScroll, spec.slidesToShow)
  }
  return indexes
}

export const checkNavigable = (spec, index) => {
  const navigables = getNavigableIndexes(spec)
  let prevNavigable = 0
  if (index > navigables[navigables.length - 1]) {
    // eslint-disable-next-line no-param-reassign
    index = navigables[navigables.length - 1]
  } else {
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const n in navigables) {
      if (index < navigables[n]) {
        // eslint-disable-next-line no-param-reassign
        index = prevNavigable
        break
      }
      prevNavigable = navigables[n]
    }
  }
  return index
}

export const safePreventDefault = (event) => {
  const passiveEvents = ['onTouchStart', 'onTouchMove', 'onWheel']
  // eslint-disable-next-line no-underscore-dangle
  if (!passiveEvents.includes(event._reactName)) {
    event.preventDefault()
  }
}

export const getOnDemandLazySlides = (spec) => {
  const onDemandSlides = []
  const startIndex = lazyStartIndex(spec)
  const endIndex = lazyEndIndex(spec)
  // eslint-disable-next-line no-plusplus
  for (let slideIndex = startIndex; slideIndex < endIndex; slideIndex++) {
    if (spec.lazyLoadedList.indexOf(slideIndex) < 0) {
      onDemandSlides.push(slideIndex)
    }
  }
  return onDemandSlides
}

export const getSwipeDirection = (touchObject, verticalSwiping = false) => {
  const xDist = touchObject.startX - touchObject.curX
  const yDist = touchObject.startY - touchObject.curY
  const r = Math.atan2(yDist, xDist)
  let swipeAngle = Math.round((r * 180) / Math.PI)
  if (swipeAngle < 0) {
    swipeAngle = 360 - Math.abs(swipeAngle)
  }
  if ((swipeAngle <= 45 && swipeAngle >= 0) || (swipeAngle <= 360 && swipeAngle >= 315)) {
    return 'left'
  }
  if (swipeAngle >= 135 && swipeAngle <= 225) {
    return 'right'
  }
  if (verticalSwiping === true) {
    if (swipeAngle >= 35 && swipeAngle <= 135) {
      return 'up'
    }
    return 'down'
  }

  return 'vertical'
}

// whether we can go next
export const canGoNext = (spec) => {
  let canGo = true
  if (!spec.infinite) {
    if (
      spec.slideCount <= spec.slidesToShow ||
      spec.currentSlide >= spec.slideCount - spec.slidesToShow
    ) {
      canGo = false
    }
  }
  return canGo
}

// given an object and a list of keys, return new object with given keys
export const extractObject = (spec, keys) => {
  const newObject = {}
  keys.forEach((key) => {
    newObject[key] = spec[key]
  })
  return newObject
}

// get initialized state
export const initializedState = (spec) => {
  // spec also contains listRef, trackRef
  const slideCount = React.Children.count(spec.children)
  const listNode = spec.listRef
  const listWidth = Math.ceil(getWidth(listNode))
  const trackNode = spec.trackRef && spec.trackRef.node
  const trackWidth = Math.ceil(getWidth(trackNode))
  let slideWidth
  if (!spec.vertical) {
    slideWidth = Math.ceil(listWidth / spec.slidesToShow)
  } else {
    slideWidth = listWidth
  }
  const slideHeight = listNode && getHeight(listNode.querySelector('[data-index="0"]'))
  const listHeight = slideHeight * spec.slidesToShow
  const currentSlide = spec.currentSlide === undefined ? spec.initialSlide : spec.currentSlide
  let lazyLoadedList = spec.lazyLoadedList || []
  const slidesToLoad = getOnDemandLazySlides({
    ...spec,
    currentSlide,
    lazyLoadedList,
  })
  lazyLoadedList = lazyLoadedList.concat(slidesToLoad)

  const state = {
    slideCount,
    slideWidth,
    listWidth,
    trackWidth,
    currentSlide,
    slideHeight,
    listHeight,
    lazyLoadedList,
  }

  if (spec.autoplaying === null && spec.autoplay) {
    state.autoplaying = 'playing'
  }

  return state
}

export const slideHandler = (spec) => {
  const { animating, infinite, index, slideCount, currentSlide, slidesToScroll, slidesToShow } =
    spec
  let { lazyLoadedList } = spec
  if (animating) return {}
  let animationSlide = index
  let finalSlide
  let animationLeft
  let state = {}
  let nextState = {}
  const targetSlide = infinite ? index : clamp(index, 0, slideCount - 1)
  finalSlide = animationSlide
  if (animationSlide < 0) {
    finalSlide = animationSlide + slideCount
    if (!infinite) finalSlide = 0
    else if (slideCount % slidesToScroll !== 0)
      finalSlide = slideCount - (slideCount % slidesToScroll)
  } else if (!canGoNext(spec) && animationSlide > currentSlide) {
    finalSlide = currentSlide
    animationSlide = currentSlide
  } else if (animationSlide >= slideCount) {
    finalSlide = animationSlide - slideCount
    if (!infinite) finalSlide = slideCount - slidesToShow
    else if (slideCount % slidesToScroll !== 0) finalSlide = 0
  }

  if (!infinite && animationSlide + slidesToShow >= slideCount) {
    finalSlide = slideCount - slidesToShow
  }

  animationLeft = getTrackLeft({ ...spec, slideIndex: animationSlide })
  const finalLeft = getTrackLeft({ ...spec, slideIndex: finalSlide })
  if (!infinite) {
    if (animationLeft === finalLeft) animationSlide = finalSlide
    animationLeft = finalLeft
  }
  lazyLoadedList = lazyLoadedList.concat(
    getOnDemandLazySlides({ ...spec, currentSlide: animationSlide }),
  )
  state = {
    animating: true,
    currentSlide: finalSlide,
    trackStyle: getTrackAnimateCSS({ ...spec, left: animationLeft }),
    lazyLoadedList,
    targetSlide,
  }
  nextState = {
    animating: false,
    currentSlide: finalSlide,
    trackStyle: getTrackCSS({ ...spec, left: finalLeft }),
    swipeLeft: null,
    targetSlide,
  }
  return { state, nextState }
}

export const changeSlide = (spec, options) => {
  let slideOffset
  let targetSlide
  const {
    slidesToScroll,
    slidesToShow,
    slideCount,
    currentSlide,
    targetSlide: previousTargetSlide,
    infinite,
  } = spec
  const unevenOffset = slideCount % slidesToScroll !== 0
  const indexOffset = unevenOffset ? 0 : (slideCount - currentSlide) % slidesToScroll
  if (options.message === 'previous') {
    slideOffset = indexOffset === 0 ? slidesToScroll : slidesToShow - indexOffset
    targetSlide = currentSlide - slideOffset
    if (!infinite) {
      targetSlide = previousTargetSlide - slidesToScroll
    }
  } else if (options.message === 'next') {
    slideOffset = indexOffset === 0 ? slidesToScroll : indexOffset
    targetSlide = currentSlide + slideOffset
    if (!infinite) {
      targetSlide = previousTargetSlide + slidesToScroll
    }
  } else if (options.message === 'children') {
    // Click on the slides
    targetSlide = options.index
    if (infinite) {
      const direction = siblingDirection({ ...spec, targetSlide })
      if (targetSlide > options.currentSlide && direction === 'left') {
        targetSlide -= slideCount
      } else if (targetSlide < options.currentSlide && direction === 'right') {
        targetSlide += slideCount
      }
    }
  } else if (options.message === 'index') {
    targetSlide = Number(options.index)
  }
  return targetSlide
}
export const keyHandler = (e, accessibility, rtl) => {
  if (e.target.tagName.match('TEXTAREA|INPUT|SELECT') || !accessibility) return ''
  if (e.keyCode === 37) return rtl ? 'next' : 'previous'
  if (e.keyCode === 39) return rtl ? 'previous' : 'next'
  return ''
}

export const swipeStart = (e, swipe) => {
  if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO') {
    safePreventDefault(e)
  }
  if (!swipe) return ''
  return {
    dragging: true,
    touchObject: {
      startX: e.touches ? e.touches[0].pageX : e.clientX,
      startY: e.touches ? e.touches[0].pageY : e.clientY,
      curX: e.touches ? e.touches[0].pageX : e.clientX,
      curY: e.touches ? e.touches[0].pageY : e.clientY,
    },
  }
}

export const swipeMove = (e, spec) => {
  // spec also contains, trackRef and slideIndex
  const {
    scrolling,
    animating,
    vertical,
    verticalSwiping,
    rtl,
    currentSlide,
    edgeFriction,
    swiping,
    infinite,
    touchObject,
    listHeight,
    listWidth,
  } = spec
  if (scrolling) return
  if (animating) {
    safePreventDefault(e)
    return
  }
  if (vertical && verticalSwiping) safePreventDefault(e)
  let swipeLeft
  let state = {}
  const curLeft = getTrackLeft(spec)
  touchObject.curX = e.touches ? e.touches[0].pageX : e.clientX
  touchObject.curY = e.touches ? e.touches[0].pageY : e.clientY
  touchObject.swipeLength = Math.round(Math.sqrt((touchObject.curX - touchObject.startX) ** 2))
  const verticalSwipeLength = Math.round(Math.sqrt((touchObject.curY - touchObject.startY) ** 2))
  if (!verticalSwiping && !swiping && verticalSwipeLength > 10) {
    // eslint-disable-next-line consistent-return
    return { scrolling: true }
  }
  if (verticalSwiping) touchObject.swipeLength = verticalSwipeLength
  let positionOffset = (!rtl ? 1 : -1) * (touchObject.curX > touchObject.startX ? 1 : -1)
  if (verticalSwiping) positionOffset = touchObject.curY > touchObject.startY ? 1 : -1

  const swipeDirection = getSwipeDirection(spec.touchObject, verticalSwiping)
  let touchSwipeLength = touchObject.swipeLength
  if (!infinite) {
    if (
      (currentSlide === 0 && (swipeDirection === 'right' || swipeDirection === 'down')) ||
      (!canGoNext(spec) && (swipeDirection === 'left' || swipeDirection === 'up'))
    ) {
      touchSwipeLength = touchObject.swipeLength * edgeFriction
    }
  }
  if (!vertical) {
    if (!rtl) {
      swipeLeft = curLeft + touchSwipeLength * positionOffset
    } else {
      swipeLeft = curLeft - touchSwipeLength * positionOffset
    }
  } else {
    swipeLeft = curLeft + touchSwipeLength * (listHeight / listWidth) * positionOffset
  }
  if (verticalSwiping) {
    swipeLeft = curLeft + touchSwipeLength * positionOffset
  }
  state = {
    ...state,
    touchObject,
    swipeLeft,
    trackStyle: getTrackCSS({ ...spec, left: swipeLeft }),
  }
  if (
    Math.abs(touchObject.curX - touchObject.startX) <
    Math.abs(touchObject.curY - touchObject.startY) * 0.8
  ) {
    // eslint-disable-next-line consistent-return
    return state
  }
  if (touchObject.swipeLength > 10) {
    state.swiping = true
    safePreventDefault(e)
  }
  // eslint-disable-next-line consistent-return
  return state
}

export const swipeEnd = (e, spec) => {
  const {
    dragging,
    swipe,
    touchObject,
    listWidth,
    touchThreshold,
    verticalSwiping,
    listHeight,
    scrolling,
    onSwipe,
    targetSlide,
    currentSlide,
    infinite,
  } = spec
  if (!dragging) {
    if (swipe) safePreventDefault(e)
    return {}
  }
  const minSwipe = verticalSwiping ? listHeight / touchThreshold : listWidth / touchThreshold
  const swipeDirection = getSwipeDirection(touchObject, verticalSwiping)
  // reset the state of touch related state variables.
  const state = {
    dragging: false,
    edgeDragged: false,
    scrolling: false,
    swiping: false,
    swipeLeft: null,
    touchObject: {},
  }
  if (scrolling) {
    return state
  }
  if (!touchObject.swipeLength) {
    return state
  }
  if (touchObject.swipeLength > minSwipe) {
    safePreventDefault(e)
    if (onSwipe) {
      onSwipe(swipeDirection)
    }
    let slideCount
    let newSlide
    const activeSlide = infinite ? currentSlide : targetSlide
    switch (swipeDirection) {
      case 'left':
      case 'up':
        newSlide = activeSlide + getSlideCount(spec)
        slideCount = checkNavigable(spec, newSlide)
        state.currentDirection = 0
        break
      case 'right':
      case 'down':
        newSlide = activeSlide - getSlideCount(spec)
        slideCount = checkNavigable(spec, newSlide)
        state.currentDirection = 1
        break
      default:
        slideCount = activeSlide
    }
    state.triggerSlideHandler = slideCount
  } else {
    // Adjust the track back to its original position.
    const currentLeft = getTrackLeft(spec)
    state.trackStyle = getTrackAnimateCSS({ ...spec, left: currentLeft })
  }
  return state
}
